var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { WMLogger } from '@mobilize/logging';
import { Constants, ErrorCode, Events, RequestType, Resolve } from '../Contract';
import { ChangeNotifier, DirtyCache, ModelCollection, ModelFactory, ModelResolver, TypeService } from '../Core';
import { HierarchyBehavior, ModalVisibilityManagementBehavior, PointerBehavior, PropagateChangeIdBehavior } from '../Core/Behaviors';
import { HttpServer, RequestBuilder, RequestConfig, UrlResolver } from '../Server';
import { NetworkException } from '../System';
import { ExceptionHandler } from '../System/Aop';
import { Invoker, ModalFactory, ViewManager } from '../UI';
import { ArrangeWindowCommand, ChangeFocusControl, ChangeMenu, ChangeStatus, CloseView, CommandFactory, Commands, ContextMenu, FileExplorerRemote, FileExplorerRemoteInput, InputBox, MessageBox, ModalCommandAlert, ModalCommandLoading, ModalCommandSessionExpired, OpenView, PopMenu, PrintScreen, QuitCurrentApplication, RefreshApplicationMenu, ServerVersionCommand, StartEdit, } from '../UI/Command';
import { ChangeMenuState } from '../UI/Command/ChangeMenuState';
import { CloseWindowByTimer } from '../UI/Command/CloseWindowByTimer';
import { DisposeWindowCommand } from '../UI/Command/DisposeWindowCommand';
import { OpenExternalLinkCommand } from '../UI/Command/OpenExternalLinkCommand';
import { SetTextToDataManagerCommand } from '../UI/Command/SetTextToDataManagerCommand';
import { SessionExpiredCommand } from '../v5/UI/Command/SessionExpired';
import { ActionModel, ActionService, Alert, DeltaHandler, EventAggregator, Inject, Library, WorkQueue } from '.';
import { MsgBox } from './../UI/Command/MsgBox';
import { CommandsWorker, ModelDeltaWorker, RemovedIdWorker, SwitchIdWorker, ViewDeltaWorker } from './DeltaWorkers';
import { PendingActionWorker } from './DeltaWorkers/PendingActionWorker';
import { Version } from './Version';
var App = (function () {
    function App(inject) {
        if (inject === void 0) { inject = null; }
        this.inject = inject || Inject.Instance;
        this.version = new Version();
        this.styleProperties = [];
    }
    App.prototype.init = function (controller, action, callback, appname) {
        if (controller === void 0) { controller = 'Home'; }
        if (action === void 0) { action = 'AppState'; }
        this.register();
        this.initResolve();
        this.loadInitialState(controller, action, callback, appname);
    };
    App.prototype.sendAction = function (action) {
        this.action.send(action);
    };
    App.prototype.sendImmediateAction = function (action) {
        this.action.sendImmediateAction(action);
    };
    App.prototype.loadInitialState = function (controller, action, callback, appname) {
        var _this = this;
        var parameters = appname ? [{ applicationName: appname }] : null;
        this.action.send(new ActionModel(controller, action, null, parameters, function () { return _this.afterInit(callback); }, new RequestConfig(RequestType.ModelRequest, true, '')));
    };
    App.prototype.isSyncronizing = function () {
        return this.modelResolver.isCoreSynchronizing();
    };
    App.prototype.executeSafely = function (data) {
        if (this.isSyncronizing()) {
            this.workQueue.addToWorkQueue(data);
        }
        else {
            data.fn();
        }
    };
    App.prototype.getTypeInfo = function (modelType) {
        return this.typeService.getTypeInfo(modelType);
    };
    App.prototype.publishEvent = function (event, data) {
        this.event.publish(event, data);
    };
    App.prototype.subscribeEvent = function (event, lambda) {
        this.event.subscribe(event, lambda);
    };
    App.prototype.removePendingView = function (viewId) {
        this.viewManager.removePendingView(viewId);
    };
    App.prototype.getPendingViews = function () {
        return this.viewManager.getPendingViews();
    };
    App.prototype.handleResponse = function (response) {
        this.deltaHandler.executeWorkers(response);
    };
    App.prototype.afterInit = function (callback) {
        if (callback === void 0) { callback = undefined; }
        if (this.onInitialized) {
            this.onInitialized();
        }
        if (callback) {
            callback();
        }
    };
    App.prototype.register = function () {
        this.inject.register(Constants.Server, HttpServer, Resolve.Singleton);
        this.inject.register(Constants.EventAggregator, EventAggregator, Resolve.Singleton);
        this.inject.register(Constants.ModelResolver, ModelResolver, Resolve.Singleton);
        this.inject.register(Constants.Action, ActionService, Resolve.Singleton);
        this.inject.register(Constants.ViewManager, ViewManager, Resolve.Singleton);
        this.inject.register(Constants.Buffer, ModelCollection, Resolve.Request);
        this.inject.register(Constants.Invoker, Invoker, Resolve.Singleton);
        this.inject.register(Constants.NotifyBuffer, DirtyCache, Resolve.Singleton);
        this.inject.register(Constants.Library, Library, Resolve.Singleton);
        this.inject.register(Constants.UrlResolver, UrlResolver, Resolve.Singleton);
        this.inject.register(Constants.ModelFactory, ModelFactory, Resolve.Singleton);
        this.inject.register(Constants.ModalFactory, ModalFactory, Resolve.Singleton);
        this.inject.register(Constants.RequestBuilder, RequestBuilder, Resolve.Singleton);
        this.inject.register(Constants.DeltaHandler, DeltaHandler, Resolve.Singleton);
        this.inject.register(Constants.WorkQueue, WorkQueue, Resolve.Singleton);
        this.inject.register(Constants.TypeService, TypeService, Resolve.Singleton);
        this.inject.register(Constants.CommandFactory, CommandFactory, Resolve.Singleton);
        this.executeOnRegister();
    };
    App.prototype.initResolve = function () {
        this.viewManager = this.inject.resolve(Constants.ViewManager);
        this.modelResolver = this.inject.resolve(Constants.ModelResolver);
        this.deltaHandler = this.inject.resolve(Constants.DeltaHandler);
        this.event = this.inject.resolve(Constants.EventAggregator);
        this.action = this.inject.resolve(Constants.Action);
        this.invoker = this.inject.resolve(Constants.Invoker);
        this.notifyBuffer = this.inject.resolve(Constants.NotifyBuffer);
        this.modelFactory = this.inject.resolve(Constants.ModelFactory);
        this.commandFactory = this.inject.resolve(Constants.CommandFactory);
        this.httpService = this.inject.resolve(Constants.Server);
        this.workQueue = this.inject.resolve(Constants.WorkQueue);
        this.typeService = this.inject.resolve(Constants.TypeService);
        this.modelResolverInit();
        this.deltaHandlerInit();
        this.executeOnInvoker();
        this.executeOnResolver();
        this.executeOnViewResolver();
        this.executeOnCommandRegister();
        this.subscribe();
        this.initModules();
    };
    App.prototype.modelResolverInit = function () {
        this.modelResolver.registerBehavior(new HierarchyBehavior());
        this.modelResolver.registerBehavior(new PointerBehavior(), true);
        this.modelResolver.registerBehavior(new PropagateChangeIdBehavior());
        this.modelResolver.registerNotifier(new ChangeNotifier());
        this.modelResolver.registerBehavior(new ModalVisibilityManagementBehavior());
    };
    App.prototype.deltaHandlerInit = function () {
        this.deltaHandler.registerWorker(new SwitchIdWorker());
        this.deltaHandler.registerWorker(new RemovedIdWorker());
        this.deltaHandler.registerWorker(new ModelDeltaWorker());
        this.deltaHandler.registerWorker(new CommandsWorker());
        this.deltaHandler.registerWorker(new ViewDeltaWorker());
        this.deltaHandler.registerWorker(new PendingActionWorker());
        this.executeDeltaHandler();
    };
    App.prototype.executeOnRegister = function () {
        if (this.onRegister) {
            this.onRegister(this.inject);
        }
    };
    App.prototype.executeDeltaHandler = function () {
        if (this.onDeltaHandler) {
            this.onDeltaHandler();
        }
    };
    App.prototype.executeOnCommandRegister = function () {
        this.commandFactory.register(Commands.OpenView, OpenView);
        this.commandFactory.register(Commands.CloseView, CloseView);
        this.commandFactory.register(Commands.MessageBox, MessageBox);
        this.commandFactory.register(Commands.MsgBox, MsgBox);
        this.commandFactory.register(Commands.InputBox, InputBox);
        this.commandFactory.register(Commands.ChangeFocusControlCommand, ChangeFocusControl);
        this.commandFactory.register(Commands.ChangeMenuCommand, ChangeMenu);
        this.commandFactory.register(Commands.ChangeStatusCommand, ChangeStatus);
        this.commandFactory.register(Commands.FileExplorerInput, FileExplorerRemoteInput);
        this.commandFactory.register(Commands.FileExplorer, FileExplorerRemote);
        this.commandFactory.register(Commands.RefreshApplicationMenu, RefreshApplicationMenu);
        this.commandFactory.register(Commands.QuitCurrentApplication, QuitCurrentApplication);
        this.commandFactory.register(Commands.PrintScreenCommand, PrintScreen);
        this.commandFactory.register(Commands.ContextMenuCommand, ContextMenu);
        this.commandFactory.register(Commands.OpenExternalLinkCommand, OpenExternalLinkCommand);
        this.commandFactory.register(Commands.ModalCommandAlert, ModalCommandAlert);
        this.commandFactory.register(Commands.SessionExpiredCommand, SessionExpiredCommand);
        this.commandFactory.register(Commands.DisposeWindowCommand, DisposeWindowCommand);
        this.commandFactory.register(Commands.ChangeMenuStateCommand, ChangeMenuState);
        this.commandFactory.register(Commands.SetTextToDataManagerCommand, SetTextToDataManagerCommand);
        this.commandFactory.register(Commands.CloseWindowByTimerCommand, CloseWindowByTimer);
        this.commandFactory.register(Commands.ServerVersionCommand, ServerVersionCommand);
        this.commandFactory.register(Commands.ArrangeWindowCommand, ArrangeWindowCommand);
        this.commandFactory.register(Commands.PopMenuCommand, PopMenu);
        this.commandFactory.register(Commands.StartEditCommand, StartEdit);
        if (this.onCommandRegister) {
            this.onCommandRegister(this.commandFactory);
        }
    };
    App.prototype.executeOnInvoker = function () {
        this.invoker.register(this.viewManager);
        if (this.onInvoker) {
            this.onInvoker(this.invoker);
        }
    };
    App.prototype.executeOnResolver = function () {
        if (this.onResolver) {
            this.onResolver(this.modelResolver);
        }
    };
    App.prototype.executeOnViewResolver = function () {
        if (this.onViewHandler) {
            this.onViewHandler(this.viewManager);
        }
    };
    App.prototype.handleError = function (exception) {
        this.invoker.invoke(new ModalCommandLoading({ show: false }));
        var message = '';
        if (exception instanceof NetworkException) {
            this.handleNetworkError(exception);
            message = 'Network Error';
        }
        else {
            this.invoker.invoke(new ModalCommandAlert(new Alert(exception.message, 'Error', '', 'error')));
            message = 'Internal Error';
        }
        WMLogger.instance().error(message);
    };
    App.prototype.handleNetworkError = function (exception) {
        if (exception.code === ErrorCode.SessionExpire) {
            this.invoker.invoke(new ModalCommandSessionExpired(new Alert(exception.message ? exception.message : 'The application will be reloaded', 'Session Expired', '', 'error')));
        }
        else if (exception.code === ErrorCode.Socket_Close_Abnormal) {
            setTimeout(function () {
                window.location.reload();
            }, 5000);
            this.invoker.invoke(new ModalCommandAlert(new Alert('The application will be reloaded', 'Connection error', '', 'error')));
        }
        else if (!!exception.message) {
            this.invoker.invoke(new ModalCommandAlert(new Alert(exception.message, 'Network Error', '', 'error')));
        }
    };
    App.prototype.subscribe = function () {
        var _this = this;
        this.event.subscribe(Events.ApplyDeltas, function (response) { return _this.handleResponse(response); });
        this.event.subscribe(Events.ActionSending, function (id) { return _this.invoker.invoke(id && id.eventName ? new ModalCommandLoading({ show: true, id: id.data, eventName: id.eventName }) :
            new ModalCommandLoading({ show: true, id: id })); });
        this.event.subscribe(Events.ActionComplete, function (id) { return _this.invoker.invoke(new ModalCommandLoading({ show: false, id: id })); });
        this.event.subscribe(Events.Error, function (exception) { return _this.handleError(exception); });
    };
    App.prototype.initModules = function () {
        var buffer = this.inject.resolve(Constants.Buffer);
        this.modelResolver.init(buffer);
        this.notifyBuffer.init(buffer);
        this.models = buffer;
        this.viewManager.init(null, buffer);
    };
    __decorate([
        ExceptionHandler(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], App.prototype, "sendAction", null);
    __decorate([
        ExceptionHandler(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], App.prototype, "sendImmediateAction", null);
    __decorate([
        ExceptionHandler(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String, String, Function, String]),
        __metadata("design:returntype", void 0)
    ], App.prototype, "loadInitialState", null);
    return App;
}());
export { App };
